import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { map, merge, switchMap, take, takeUntil, tap, throwError } from 'rxjs';

import { SocketContext } from '../../../../providers/SocketProvider/SocketProvider';

export interface PromptVariables {
  learningObjective: string;
  grade: string;
  subject?: string;
  languages?: string[];
  lessonCategory?:
    | 'teachSomethingNew'
    | 'reviewOrReteach'
    | 'practiceASkill'
    | 'readAloud'
    | 'closeReading'
    | 'other';
  standardAlignment?: string;
}

export const useLearningObjectiveRefinement = () => {
  const { gameAPI } = useContext(SocketContext);
  const cleanupRef = useRef<(() => void) | null>(null);
  const [streamData, setStreamData] = useState<{
    tokens: string[];
    isComplete: boolean;
    currentContent: string;
    state: 'thinking' | 'generating' | undefined;
  }>({
    tokens: [],
    isComplete: false,
    currentContent: '',
    state: undefined,
  });
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<{
    status: number;
    name: string;
    type: string;
    errors?: { message: string }[];
  } | null>(null);
  const [isObjectiveReady, setIsObjectiveReady] = useState(false);
  const [promptVariables, setPromptVariables] = useState<PromptVariables | null>(null);
  const [sessionLength, setSessionLength] = useState<number | null>(null);
  const [llmThreadId, setLlmThreadId] = useState<string>();

  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (gameAPI) {
      if (gameAPI.getSocket().connected) {
        setIsConnected(true);
        if (error && error.type === 'DisconnectError') {
          setError(null);
        }
      }
      //Establish listeners for connect and disconnect events
      const connectObservable = gameAPI.createEventObservable('connect').subscribe(() => {
        setIsConnected(true);
        if (error && error.type === 'DisconnectError') {
          setError(null);
        }
      });
      const disconnectObservable = gameAPI
        .createEventObservable('disconnect')
        .subscribe(() => {
          setIsConnected(false);
          setError({
            status: 500,
            name: 'Disconnected',
            type: 'DisconnectError',
            errors: [
              {
                message:
                  'Disconnected from the server. Please wait a moment and try again.',
              },
            ],
          });
        });

      return () => {
        connectObservable.unsubscribe();
        disconnectObservable.unsubscribe();
      };
    }
  }, [gameAPI, error]);

  const startStream = useCallback(
    (message: string) => {
      if (isStreaming) {
        return;
      }

      // Clean up any existing subscription before starting a new one
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }

      // Reset state
      setStreamData({
        tokens: [],
        isComplete: false,
        currentContent: '',
        state: 'thinking',
      });
      setIsStreaming(true);
      setError(null);

      if (!gameAPI) {
        setError({
          status: 400,
          name: 'Bad Request',
          type: 'error',
          errors: [
            {
              message:
                'Learning objective refinement is not available in this context. Please use the lesson editor to access this feature.',
            },
          ],
        });
        setIsStreaming(false);
        return;
      }

      // Only take the first connection event, then unsubscribe
      const streamObservable = gameAPI.ensureConnected().pipe(
        take(1),
        tap(() => {
          // Emit learning objective message event (this is the correct event name)
          gameAPI
            .getSocket()
            .emit(
              'learning-objective:message',
              { message, llmThreadId, promptVariables },
              (res: {
                status: number;
                error?: { status: number; message: string };
                data?: { message: string; sessionId: string; llmThreadId: string };
              }) => {
                if (res.error) {
                  setError({
                    status: res.error.status,
                    name: 'Error',
                    type: 'error',
                    errors: [{ message: res.error.message }],
                  });
                  setIsStreaming(false);
                }
                // Store this immediately so we can use it in the next message
                if (res.data?.llmThreadId) {
                  setLlmThreadId(res.data.llmThreadId);
                }
              },
            );
        }),
        switchMap(() => {
          // Create observables for each event type
          const tokenObservable = gameAPI
            .createEventObservable<string>('learning-objective:stream:onToken')
            .pipe(map(tokenData => ({ type: 'token' as const, data: tokenData })));

          const streamingStateObservable = gameAPI
            .createEventObservable<{ state: 'thinking' | 'generating' | undefined }>(
              'learning-objective:stream:state',
            )
            .pipe(map(stateData => ({ type: 'streamState' as const, data: stateData })));

          // Main event - assistant message added with isObjectiveReady flag
          const assistantMessageAddedObservable = gameAPI
            .createEventObservable<{
              content: string;
              isObjectiveReady?: boolean;
              llmThreadId?: string;
            }>('learning-objective:stream:assistantMessageAdded')
            .pipe(
              map(messageData => ({
                type: 'assistantMessageAdded' as const,
                data: messageData,
              })),
            );

          const completeObservable = gameAPI
            .createEventObservable<{
              message: string;
              isObjectiveReady: boolean;
              finishReason: string;
              promptVariables: PromptVariables;
              llmThreadId: string;
              sessionLength: number;
            }>('learning-objective:stream:complete')
            .pipe(
              tap(data => {
                // Check for isObjectiveReady right here in the stream
                if (data.isObjectiveReady === true) {
                  setIsObjectiveReady(true);

                  // Also capture promptVariables if available
                  if (data.promptVariables) {
                    setPromptVariables(data.promptVariables);
                  }
                }

                // Capture sessionLength if available
                if (data.sessionLength !== undefined) {
                  setSessionLength(data.sessionLength);
                }
              }),
              map(completeData => ({ type: 'complete' as const, data: completeData })),
            );

          const errorObservable = gameAPI
            .createEventObservable<{
              error: {
                status: number;
                name: string;
                type: string;
                errors?: { message: string }[];
              };
            }>('learning-objective:stream:error')
            .pipe(
              map(errorData => ({
                type: 'error' as const,
                data: errorData,
              })),
              switchMap(errorEvent => throwError(() => errorEvent.data.error)),
            );

          const disconnectedObservable = gameAPI
            .createEventObservable<boolean>('disconnect')
            .pipe(
              map(() => ({ type: 'disconnected' as const })),
              switchMap(() =>
                throwError(() => ({
                  status: 500,
                  name: 'Disconnected',
                  type: 'DisconnectError',
                  errors: [
                    {
                      message:
                        'Disconnected from the server. Please wait a moment and try again.',
                    },
                  ],
                })),
              ),
            );

          // Merge all observables and complete when the complete event fires
          return merge(
            tokenObservable,
            streamingStateObservable,
            assistantMessageAddedObservable,
            completeObservable,
            errorObservable,
            disconnectedObservable,
          ).pipe(
            takeUntil(completeObservable.pipe(take(1))), // Complete when complete event fires
          );
        }),
      );

      const subscription = streamObservable.subscribe({
        next: event => {
          if (event.type === 'token') {
            setStreamData(prev => ({
              ...prev,
              tokens: [...prev.tokens, event.data],
              currentContent: prev.currentContent + event.data,
            }));
          }

          if (event.type === 'streamState') {
            setStreamData(prev => ({
              ...prev,
              state: event.data.state,
            }));
          }

          if (event.type === 'complete') {
            // Note: isObjectiveReady is already handled in the observable stream above
          }
        },
        error: (err: {
          status: number;
          name: string;
          type: string;
          errors?: { message: string }[];
        }) => {
          setError(err);
          setStreamData({
            currentContent: '',
            isComplete: false,
            state: undefined,
            tokens: [],
          });
          setIsStreaming(false);
        },
        complete: () => {
          setIsStreaming(false);
          setStreamData(prev => ({
            ...prev,
            isComplete: true,
          }));
        },
      });

      // Create cleanup function
      const cleanup = () => {
        subscription.unsubscribe();
        setIsStreaming(false);
        cleanupRef.current = null;
      };

      // Store cleanup function in ref and return it
      cleanupRef.current = cleanup;
      return cleanup;
    },
    [gameAPI, isStreaming, llmThreadId, promptVariables],
  );

  const resetStream = useCallback(() => {
    setStreamData({
      tokens: [],
      isComplete: false,
      currentContent: '',
      state: undefined,
    });
    setIsStreaming(false);
    setError(null);
  }, []);

  const stopStream = useCallback(() => {
    // Clean up active subscription
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }

    if (gameAPI) {
      gameAPI.getSocket().emit('learning-objective:stream:stop');
    }
    resetStream();
  }, [gameAPI, resetStream]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, []);

  return {
    streamData,
    isStreaming,
    error,
    isObjectiveReady,
    promptVariables,
    sessionLength,
    startStream,
    resetStream,
    stopStream,
    setIsObjectiveReady,
    isConnected,
  };
};
